import {
  StorageLayer,
  StorageOptions,
  StorageResult,
  StorageStatus,
  StorageError,
  StorageErrorCode,
} from "./types";
import { validationSystem } from "./validation";
import { loggingSystem } from "./logging";
import { logger } from "../../utils/logger";

/**
 * Base storage implementation with common functionality
 */
export abstract class BaseStorage implements StorageLayer {
  protected constructor(
    protected readonly name: string,
    protected readonly options: StorageOptions = {
      operation: {
        type: "read",
        source: name,
        timestamp: Date.now(),
      },
    },
  ) {}

  /**
   * Save data to storage
   */
  async save<T>(
    key: string,
    data: T,
    options?: StorageOptions,
  ): Promise<StorageResult<void>> {
    const startTime = Date.now();

    try {
      // Validate key
      const keyValidation = validationSystem.validateKey(key);
      if (!keyValidation.isValid) {
        throw new StorageError(
          "Invalid storage key",
          StorageErrorCode.INVALID_DATA,
          { errors: keyValidation.errors },
        );
      }

      // Validate data
      const dataValidation = validationSystem.validateData(data, options);
      if (!dataValidation.isValid) {
        throw new StorageError(
          "Invalid data",
          StorageErrorCode.VALIDATION_FAILED,
          { errors: dataValidation.errors },
        );
      }

      // Check storage availability
      const status = await this.getStatus();
      if (!status.available) {
        throw new StorageError(
          "Storage not available",
          StorageErrorCode.NOT_AVAILABLE,
          { status },
        );
      }

      // Perform storage-specific save
      await this.performSave(key, data, options);

      // Log success
      loggingSystem.logOperation("save", key, startTime, "success", undefined, {
        storageType: this.name,
        dataType: typeof data,
      });

      return {
        success: true,
        metadata: {
          timestamp: Date.now(),
          operation: "save",
          size: this.calculateSize(data),
        },
      };
    } catch (error) {
      // Handle and log error
      const storageError = this.handleError(error);
      loggingSystem.logOperation(
        "save",
        key,
        startTime,
        "failure",
        storageError,
        { storageType: this.name },
      );

      return {
        success: false,
        error: storageError,
        metadata: {
          timestamp: Date.now(),
          operation: "save",
        },
      };
    }
  }

  /**
   * Load data from storage
   */
  async load<T>(
    key: string,
    options?: StorageOptions,
  ): Promise<StorageResult<T>> {
    const startTime = Date.now();

    try {
      // Validate key
      const keyValidation = validationSystem.validateKey(key);
      if (!keyValidation.isValid) {
        throw new StorageError(
          "Invalid storage key",
          StorageErrorCode.INVALID_DATA,
          { errors: keyValidation.errors },
        );
      }

      // Check storage availability
      const status = await this.getStatus();
      if (!status.available) {
        throw new StorageError(
          "Storage not available",
          StorageErrorCode.NOT_AVAILABLE,
          { status },
        );
      }

      // Perform storage-specific load
      const data = await this.performLoad<T>(key, options);

      // Log success
      loggingSystem.logOperation("load", key, startTime, "success", undefined, {
        storageType: this.name,
      });

      return {
        success: true,
        data,
        metadata: {
          timestamp: Date.now(),
          operation: "load",
          size: this.calculateSize(data),
        },
      };
    } catch (error) {
      // Handle and log error
      const storageError = this.handleError(error);
      loggingSystem.logOperation(
        "load",
        key,
        startTime,
        "failure",
        storageError,
        { storageType: this.name },
      );

      return {
        success: false,
        error: storageError,
        ...(storageError.metadata?.notFound === true && { notFound: true }),
        metadata: {
          timestamp: Date.now(),
          operation: "load",
        },
      };
    }
  }

  /**
   * Delete data from storage
   */
  async delete(
    key: string,
    options?: StorageOptions,
  ): Promise<StorageResult<void>> {
    const startTime = Date.now();

    try {
      // Validate key
      const keyValidation = validationSystem.validateKey(key);
      if (!keyValidation.isValid) {
        throw new StorageError(
          "Invalid storage key",
          StorageErrorCode.INVALID_DATA,
          { errors: keyValidation.errors },
        );
      }

      // Check storage availability
      const status = await this.getStatus();
      if (!status.available) {
        throw new StorageError(
          "Storage not available",
          StorageErrorCode.NOT_AVAILABLE,
          { status },
        );
      }

      // Perform storage-specific delete
      await this.performDelete(key, options);

      // Log success
      loggingSystem.logOperation(
        "delete",
        key,
        startTime,
        "success",
        undefined,
        { storageType: this.name },
      );

      return {
        success: true,
        metadata: {
          timestamp: Date.now(),
          operation: "delete",
        },
      };
    } catch (error) {
      // Handle and log error
      const storageError = this.handleError(error);
      loggingSystem.logOperation(
        "delete",
        key,
        startTime,
        "failure",
        storageError,
        { storageType: this.name },
      );

      return {
        success: false,
        error: storageError,
        metadata: {
          timestamp: Date.now(),
          operation: "delete",
        },
      };
    }
  }

  /**
   * Clear all data from storage
   */
  async clear(options?: StorageOptions): Promise<StorageResult<void>> {
    const startTime = Date.now();

    try {
      // Check storage availability
      const status = await this.getStatus();
      if (!status.available) {
        throw new StorageError(
          "Storage not available",
          StorageErrorCode.NOT_AVAILABLE,
          { status },
        );
      }

      // Perform storage-specific clear
      await this.performClear(options);

      // Log success
      loggingSystem.logOperation(
        "clear",
        "all",
        startTime,
        "success",
        undefined,
        { storageType: this.name },
      );

      return {
        success: true,
        metadata: {
          timestamp: Date.now(),
          operation: "clear",
        },
      };
    } catch (error) {
      // Handle and log error
      const storageError = this.handleError(error);
      loggingSystem.logOperation(
        "clear",
        "all",
        startTime,
        "failure",
        storageError,
        { storageType: this.name },
      );

      return {
        success: false,
        error: storageError,
        metadata: {
          timestamp: Date.now(),
          operation: "clear",
        },
      };
    }
  }

  /**
   * Get storage status
   */
  abstract getStatus(): Promise<StorageStatus>;

  /**
   * Perform storage-specific save operation
   */
  protected abstract performSave<T>(
    key: string,
    data: T,
    options?: StorageOptions,
  ): Promise<void>;

  /**
   * Perform storage-specific load operation
   */
  protected abstract performLoad<T>(
    key: string,
    options?: StorageOptions,
  ): Promise<T>;

  /**
   * Perform storage-specific delete operation
   */
  protected abstract performDelete(
    key: string,
    options?: StorageOptions,
  ): Promise<void>;

  /**
   * Perform storage-specific clear operation
   */
  protected abstract performClear(options?: StorageOptions): Promise<void>;

  /**
   * Calculate size of data in bytes
   */
  protected calculateSize(data: unknown): number {
    try {
      const str = JSON.stringify(data);
      return str.length * 2; // Unicode characters
    } catch (error) {
      logger.warn("Failed to calculate data size", {
        error: error instanceof Error ? error : new Error(String(error)),
      });
      return 0;
    }
  }

  /**
   * Handle and convert errors
   */
  protected handleError(error: unknown): StorageError {
    if (error instanceof StorageError) {
      return error;
    }

    if (error instanceof Error) {
      return new StorageError(
        error.message,
        StorageErrorCode.OPERATION_FAILED,
        { originalError: error },
      );
    }

    return new StorageError(String(error), StorageErrorCode.UNKNOWN_ERROR);
  }
}
